import React, { useState, useEffect, useCallback } from 'react'
import { Group } from '@mantine/core'
import { AppIconButton, AppMenu, AppText } from '../../app/common/ui'
import {
  IoHeart,
  IoHeartOutline,
  IoChatbubbleOutline,
  IoShareSocialOutline,
  IoEllipsisHorizontal
} from 'react-icons/io5'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { SlopDiscoverCard } from '@gunslop/ui'
import agent from '../../app/api/agent'
import { FlagTargetType } from '../../app/models/flag'
import { toAbsoluteMediaUrl } from '../../app/api/resolveApiBaseUrl'
import { CommentsDrawer } from './CommentsDrawer'

export interface SlopDiscoverPostDto {
  id: string;
  userId: string;
  videoUrl: string;
  thumbnail: string;
  title: string;
  createdAt: string;
  userAvatar: string;
  userName: string;
  likeCount: number;
  commentCount: number;
  linkedCaption?: string;
}

interface SlopDiscoverPostCardProps {
  post: SlopDiscoverPostDto;
  onVideoClick?: (postId: string, videoUrl: string) => void;
  onLike?: (postId: string, liked: boolean) => Promise<void>;
  onCommentClick?: (postId: string) => void;
  onShare?: (postId: string) => void;
}

function getRelativeTime (dateString: string): string {
  const diffMs = Date.now() - new Date(dateString).getTime()
  const minutes = Math.floor(diffMs / (1000 * 60))
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

export const SlopDiscoverPostCard: React.FC<SlopDiscoverPostCardProps> = ({
  post,
  onVideoClick,
  onLike,
  onCommentClick,
  onShare
}) => {
  const history = useHistory()
  const [isLiked, setIsLiked] = useState(false)
  const [isLoadingLike, setIsLoadingLike] = useState(false)
  const [likeCount, setLikeCount] = useState(post.likeCount)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [commentCount, setCommentCount] = useState(post.commentCount)

  const submitReport = async (reason: string) => {
    const r = reason.trim()
    if (!r) {
      toast.error('Please add a reason.')
      return
    }
    try {
      await agent.Flags.submit({ targetType: FlagTargetType.SlopPost, targetId: post.id, reason: r })
      toast.success('Report submitted.')
    } catch {
      /* interceptor */
    }
  }

  const posterSrc = toAbsoluteMediaUrl(post.thumbnail || post.videoUrl)

  useEffect(() => {
    setLikeCount(post.likeCount)
    setCommentCount(post.commentCount)
  }, [post.id, post.likeCount, post.commentCount])

  const openVideo = () => {
    if (onVideoClick) onVideoClick(post.id, post.videoUrl)
    else history.push(`/slop-feed/${post.id}`)
  }

  const handleLike = useCallback(async () => {
    if (isLoadingLike) return
    setIsLoadingLike(true)
    const prevLiked = isLiked
    const prevCount = likeCount
    try {
      if (onLike) {
        const next = !isLiked
        setIsLiked(next)
        await onLike(post.id, next)
      } else {
        const res = await agent.SlopFeed.toggleLike(post.id)
        setIsLiked(res.isLiked)
        setLikeCount(res.newLikeCount)
      }
    } catch (error) {
      setIsLiked(prevLiked)
      setLikeCount(prevCount)
      console.error('Failed to like post:', error)
    } finally {
      setIsLoadingLike(false)
    }
  }, [isLiked, isLoadingLike, likeCount, onLike, post.id])

  const openComments = () => {
    if (onCommentClick) {
      onCommentClick(post.id)
      return
    }
    setCommentsOpen(true)
  }

  const actionBar = (
    <>
      <AppIconButton
        variant="subtle"
        size="lg"
        sx={{ '@media (max-width: 575px)': { width: 44, height: 44 } }}
        color={isLiked ? 'red' : 'gray'}
        onClick={(e) => {
          e.stopPropagation()
          void handleLike()
        }}
        disabled={isLoadingLike}
        aria-label={isLiked ? 'Unlike' : 'Like'}
      >
        {isLiked ? <IoHeart size={22} /> : <IoHeartOutline size={22} />}
      </AppIconButton>
      <AppIconButton
        variant="subtle"
        size="lg"
        color="gray"
        onClick={(e) => {
          e.stopPropagation()
          openComments()
        }}
        aria-label="Comments"
      >
        <IoChatbubbleOutline size={22} />
      </AppIconButton>
      <AppIconButton
        variant="subtle"
        size="lg"
        color="gray"
        onClick={(e) => {
          e.stopPropagation()
          onShare?.(post.id)
        }}
        aria-label="Share"
        sx={{ marginLeft: 'auto' }}
      >
        <IoShareSocialOutline size={22} />
      </AppIconButton>
      <AppMenu shadow="md" width={180} withinPortal>
        <AppMenu.Target>
          <AppIconButton
            variant="subtle"
            size="lg"
            color="gray"
            onClick={(e) => e.stopPropagation()}
            aria-label="More actions"
          >
            <IoEllipsisHorizontal size={22} />
          </AppIconButton>
        </AppMenu.Target>
        <AppMenu.Dropdown>
          <AppMenu.Item
            onClick={(e) => {
              e.stopPropagation()
              const reason = window.prompt('Describe the issue for moderators:')
              if (reason) void submitReport(reason)
            }}
          >
            Report post
          </AppMenu.Item>
        </AppMenu.Dropdown>
      </AppMenu>
      <Group spacing={6} style={{ flexShrink: 0 }}>
        <AppText size="xs" c="dimmed">
          {likeCount} {likeCount === 1 ? 'like' : 'likes'}
        </AppText>
        <AppText size="xs" c="dimmed">
          ·
        </AppText>
        <AppText size="xs" c="dimmed">
          {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
        </AppText>
      </Group>
    </>
  )

  return (
    <>
      <SlopDiscoverCard
        posterUrl={posterSrc}
        title={post.title}
        userName={post.userName}
        userAvatar={post.userAvatar}
        linkedCaption={post.linkedCaption}
        timeLabel={getRelativeTime(post.createdAt)}
        onOpen={openVideo}
        actionBar={actionBar}
      />

      <CommentsDrawer
        opened={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        target="slop"
        contentId={post.id}
        onCommentCountChange={setCommentCount}
      />
    </>
  )
}

export default SlopDiscoverPostCard

/** @deprecated Use SlopDiscoverPostDto */
export type PostDto = SlopDiscoverPostDto
